"use client";
import * as React from "react";
import { DataTable, type Column } from "../DataTable";
import { PageHeader, StatCard, Card2 } from "../ModuleHelpers";
import { useERP, formatNum, formatBDT, type ID } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Plus, Camera, CheckCircle2, XCircle, Eye } from "lucide-react";

export function TMSModule() {
  const erp = useERP();
  const { toast } = useToast();
  const [tab, setTab] = React.useState("trips");
  const [podShipmentId, setPodShipmentId] = React.useState<string | null>(null);

  const trips = erp.shipments.map(sh => {
    const so = erp.salesOrders.find(s => s.id === sh.soId);
    const v = erp.vehicles.find(x => x.id === sh.vehicleId);
    const d = erp.employees.find(e => e.id === sh.driverId);
    const cust = so ? erp.getCustomer(so.customerId) : null;
    return {
      id: sh.id, shipment: sh.shipmentNumber, so: so?.soNumber ?? "—",
      vehicle: v?.regNumber ?? "—", driver: d?.name ?? "—",
      customer: cust?.name ?? "—", zone: cust?.zone ?? "—",
      dispatchDate: sh.dispatchDate, deliveryDate: sh.deliveryDate ?? "—",
      stops: sh.stops, status: sh.status, codAmount: sh.codAmount,
      hasPOD: !!sh.podSignature,
    };
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Transport & Fleet" desc="Active trips, fleet master and POD tracking." icon={Truck} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active Trips" value={formatNum(trips.filter(t => t.status === "Dispatched" || t.status === "In Transit").length)} tone="text-primary" />
        <StatCard label="Delivered" value={formatNum(trips.filter(t => t.status === "Delivered").length)} />
        <StatCard label="Fleet Size" value={formatNum(erp.vehicles.length)} />
        <StatCard label="Failed" value={formatNum(trips.filter(t => t.status === "Failed").length)} tone="text-destructive" />
      </div>

      <div className="grid grid-cols-2 gap-2 sm:w-fit">
        <Button size="sm" variant={tab === "trips" ? "default" : "outline"} onClick={() => setTab("trips")}>Active Trips</Button>
        <Button size="sm" variant={tab === "fleet" ? "default" : "outline"} onClick={() => setTab("fleet")}>Fleet Master</Button>
      </div>

      {tab === "trips" && (
        <Card2 title={`Active Trips (${trips.length})`}>
          <DataTable
            columns={[
              { key: "shipment", header: "Shipment #", sortable: true, render: r => <span className="font-mono text-xs text-primary">{r.shipment}</span> },
              { key: "so", header: "SO #" },
              { key: "customer", header: "Customer" },
              { key: "zone", header: "Zone" },
              { key: "vehicle", header: "Vehicle", render: r => <span className="font-mono text-xs">{r.vehicle}</span> },
              { key: "driver", header: "Driver" },
              { key: "dispatchDate", header: "Dispatch" },
              { key: "codAmount", header: "COD", align: "right", render: r => r.codAmount > 0 ? <span className="font-medium text-amber-600">{formatBDT(r.codAmount)}</span> : "—" },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Delivered" ? "default" : r.status === "Failed" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  {(r.status === "Dispatched" || r.status === "In Transit") && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => setPodShipmentId(r.id)}><Camera className="size-3 mr-1" />Capture POD</Button>
                  )}
                  {r.status === "Delivered" && r.hasPOD && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => toast({ title: "POD Captured", description: `Signature on file for ${r.shipment}.` })}><Eye className="size-3 mr-1" />View POD</Button>
                  )}
                </div>
              )},
            ] as Column<typeof trips[number]>[]}
            data={trips}
            rowKey={r => r.id}
            searchKeys={["shipment", "so", "vehicle", "driver", "customer"]}
          />
        </Card2>
      )}

      {tab === "fleet" && (
        <Card2 title={`Fleet Master (${erp.vehicles.length})`}>
          <DataTable
            columns={[
              { key: "regNumber", header: "Reg #", sortable: true, render: r => <span className="font-mono text-xs">{r.regNumber}</span> },
              { key: "type", header: "Type" },
              { key: "capacity", header: "Capacity" },
              { key: "driver", header: "Assigned Driver", render: r => r.driverId ? erp.employees.find(e => e.id === r.driverId)?.name ?? "—" : "—" },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Available" ? "default" : r.status === "On Trip" ? "secondary" : "outline"}>{r.status}</Badge> },
            ] as Column<typeof erp.vehicles[number]>[]}
            data={erp.vehicles}
            rowKey={r => r.id}
            searchKeys={["regNumber", "type"]}
          />
        </Card2>
      )}

      <PODDialog shipmentId={podShipmentId} onOpenChange={(v) => !v && setPodShipmentId(null)} />
    </div>
  );
}

// ===== POD Capture Dialog (reused from GIS but standalone) =====
function PODDialog({ shipmentId, onOpenChange }: { shipmentId: string | null; onOpenChange: (v: boolean) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [signature, setSignature] = React.useState("");
  const [cod, setCod] = React.useState("0");
  const ship = erp.shipments.find(s => s.id === shipmentId);
  React.useEffect(() => { if (ship) { setSignature(""); setCod(String(ship.codAmount)); } }, [shipmentId]);

  if (!ship) return null;
  const so = erp.salesOrders.find(s => s.id === ship.soId);
  const cust = so ? erp.getCustomer(so.customerId) : null;
  const vehicle = erp.vehicles.find(v => v.id === ship.vehicleId);

  return (
    <Dialog open={!!shipmentId} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="size-5" /> Capture Proof of Delivery</DialogTitle>
          <DialogDescription>Mark shipment {ship.shipmentNumber} as delivered.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Shipment:</span><span className="font-mono">{ship.shipmentNumber}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Customer:</span><span className="font-medium">{cust?.name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Zone:</span><span>{cust?.zone}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Vehicle:</span><span className="font-mono">{vehicle?.regNumber}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Stops:</span><span>{ship.stops}</span></div>
          </div>
          <div>
            <Label htmlFor="tms-sig">Receiver Signature (type name)</Label>
            <Input id="tms-sig" value={signature} onChange={e => setSignature(e.target.value)} placeholder="e.g. Rahim Uddin (Receiver)" autoFocus />
          </div>
          <div>
            <Label htmlFor="tms-cod">COD Amount Collected (৳)</Label>
            <Input id="tms-cod" type="number" value={cod} onChange={e => setCod(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => {
            if (!signature.trim()) { toast({ title: "Signature required", variant: "destructive" }); return; }
            erp.capturePOD(ship.id, signature.trim(), Number(cod) || 0);
            toast({ title: "POD Captured", description: `${ship.shipmentNumber} marked Delivered. COD: ${formatBDT(Number(cod) || 0)}. Vehicle released.` });
            onOpenChange(false);
          }}><CheckCircle2 className="size-4 mr-1" /> Confirm Delivery</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
